import { ArrowsClockwise, Download, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useAutoUpdater } from '@/hooks/useAutoUpdater';
import { createLogger } from '@/lib/logger';
import { isTauri } from '@/lib/utils/tauri';
import { useUpdateStore } from '@/store/update.store';
import { TAURI_TITLE_BAR_HEIGHT } from '@/components/layout/TauriTitleBar';

const logger = createLogger('components/shared/UpdateBanner');

/**
 * Update Banner Component
 * Shows a persistent banner at the top of the app when an update is available
 * Only visible in desktop (Tauri) environment
 */

export const UPDATE_BANNER_HEIGHT = 40;

export function useUpdateBannerVisible() {
  const updateAvailable = useUpdateStore(state => state.updateAvailable);
  const dismissed = useUpdateStore(state => state.dismissed);

  return isTauri() && updateAvailable && !dismissed;
}

export function UpdateBanner() {
  const {
    updateInfo,
    installing,
    updateDownloaded,
    downloadProgress: predownloadProgress,
    dismissUpdate,
  } = useUpdateStore();
  const { installUpdate } = useAutoUpdater({ enableListeners: false });

  const isVisible = useUpdateBannerVisible();
  const downloadProgress = updateDownloaded ? 100 : predownloadProgress;
  if (!isVisible) {
    return null;
  }

  const handleInstall = async () => {
    try {
      if (!isTauri()) {
        return;
      }

      await installUpdate();
    } catch (error) {
      logger.error('Update installation failed:', error);
    }
  };

  return (
    <div className="relative flex h-10 w-full items-center justify-center gap-4 bg-blue-600 px-4 text-white shadow-sm">
      <div className="flex items-center gap-2">
        <Download className="h-4 w-4" weight="bold" />
        <span className="truncate text-sm font-medium">
          A new version ({updateInfo?.version}) is available!
        </span>
      </div>

      <Button
        size="sm"
        variant="default"
        onClick={handleInstall}
        disabled={installing}
        className="h-7 px-3 text-xs font-medium gap-1 rounded-full bg-blue-600 text-white hover:bg-blue-500"
      >
        {installing ? (
          <>
            <ArrowsClockwise className="h-3 w-3 mr-1 animate-spin" />
            {downloadProgress > 0 ? 'Installing...' : 'Downloading...'}
          </>
        ) : (
          <>
            <Download className="h-3 w-3 mr-1" weight="bold" />
            Update
          </>
        )}
      </Button>

      <button
        onClick={dismissUpdate}
        className="absolute right-4 rounded p-1 transition-colors hover:bg-primary-foreground/10"
        title="Dismiss (update will still be available)"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export function TauriUpdateBannerSlot() {
  const isVisible = useUpdateBannerVisible();

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className="fixed left-0 right-0 z-[49]"
      style={{ top: TAURI_TITLE_BAR_HEIGHT }}
    >
      <UpdateBanner />
    </div>
  );
}
